import { supabase } from '../../lib/supabase';
import type { Profile } from '../../types/database';
import { calculateDistance } from '../../utils/distance';

export type MusicianWithDistance = Profile & {
  distance?: number;
};

export async function fetchFeaturedMusicians(
  limit: number = 5,
  coordinates?: { latitude: number; longitude: number } | null
) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('is_musician', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    // Add distances if coordinates are provided
    const musiciansWithDistance = (data || []).map(musician => {
      if (coordinates && musician.coordinates) {
        return {
          ...musician,
          distance: calculateDistance(coordinates, musician.coordinates),
        };
      }
      return musician;
    });

    return musiciansWithDistance;
  } catch (error) {
    console.error('Error fetching featured musicians:', error);
    throw error;
  }
}

export async function fetchMusicians(
  page: number,
  itemsPerPage: number,
  searchQuery?: string,
  selectedGenres?: string[],
  selectedSkills?: string[],
  selectedLocations?: string[],
  selectedStatuses?: string[],
  coordinates?: { latitude: number; longitude: number } | null,
  nearMeEnabled?: boolean
) {
  try {
    let query = supabase
      .from('profiles')
      .select('*', { count: 'exact' })
      .eq('is_musician', true);

    // Add search filter
    if (searchQuery && searchQuery.trim() !== '') {
      query = query.or(`artist_name.ilike.%${searchQuery.trim()}%,full_name.ilike.%${searchQuery.trim()}%,location.ilike.%${searchQuery.trim()}%`);
    }

    // Add genre filter
    if (selectedGenres && selectedGenres.length > 0) {
      query = query.contains('genres', selectedGenres);
    }

    // Add skills filter
    if (selectedSkills && selectedSkills.length > 0) {
      query = query.contains('skills', selectedSkills);
    }

    // Add location filter
    if (selectedLocations && selectedLocations.length > 0) {
      query = query.in('location', selectedLocations);
    }

    // Add status filter
    if (selectedStatuses && selectedStatuses.length > 0) {
      query = query.in('status', selectedStatuses);
    }

    // Always order by coordinates first (non-null first), then by creation date
    query = query
      .order('coordinates', { ascending: false, nullsLast: true })
      .order('created_at', { ascending: false });

    const start = page * itemsPerPage;
    query = query.range(start, start + itemsPerPage - 1);

    const { data, count, error } = await query;

    if (error) throw error;

    // Add distances if coordinates are provided
    const musiciansWithDistance = (data || []).map(musician => {
      if (nearMeEnabled && coordinates && musician.coordinates) {
        return {
          ...musician,
          distance: calculateDistance(coordinates, musician.coordinates),
        };
      }
      return musician;
    });

    // Sort by distance if near me is enabled
    if (nearMeEnabled && coordinates) {
      musiciansWithDistance.sort((a, b) => {
        // Both have coordinates - sort by distance
        if (a.coordinates && b.coordinates) {
          return (a.distance || 0) - (b.distance || 0);
        }
        // One has coordinates - prioritize the one with coordinates
        if (a.coordinates) return -1;
        if (b.coordinates) return 1;
        // Neither has coordinates - maintain original order
        return 0;
      });
    }

    return { musicians: musiciansWithDistance, totalCount: count };
  } catch (error) {
    console.error('Error fetching musicians:', error);
    throw error;
  }
}